import { getParentBySelector } from 'common_lib/utils'
import { FileRec, MediaItem } from 'mytours-read-api-typescript'
import { ionHistory } from '../navigation'
import { getUAParser } from '../../services/uaParser'
import { isIOSSafariBrowser } from '../platform'

export const cleanText = (str: string): string => (str ? str.replace(/<\/?[^>]+(>|$)/g, '') : '')

export const getImageAlt = ({ description, caption, file = {} as FileRec }: MediaItem): string => {
	if (description) {
		return cleanText(description)
	}

	if (caption) {
		return cleanText(caption)
	}

	if (file?.filename) {
		return cleanText(file?.filename)
	}

	return ''
}

export const getParentOrLast = (element: HTMLElement | undefined, selector: string): HTMLElement | undefined => {
	const parent = element && getParentBySelector(element, selector)
	if (parent) {
		return parent
	} else {
		const elements = document.querySelectorAll(selector)
		return elements?.length ? (elements[elements.length - 1] as HTMLElement) : undefined
	}
}

export const getPageContainer = (element?: HTMLElement): HTMLElement | undefined =>
	getParentOrLast(element, '.page-content')

export const getRouterOutlet = (element?: HTMLElement): HTMLElement | undefined =>
	getParentOrLast(element, 'ion-router-outlet')

export const getScrollbarWidth = (element: HTMLElement): number | undefined => {
	const parent = element?.parentElement

	if (!element || !parent) {
		return undefined
	}

	return Math.max((parent.clientWidth || 0) - (element.clientWidth || 0), 0)
}

export const getPageScrollbarWidth = (element?: HTMLElement): number | undefined => {
	const pageContainer = getPageContainer(element)

	const pageContainerParent = pageContainer?.parentElement

	if (!pageContainer || !pageContainerParent) {
		return undefined
	}
	return Math.max((pageContainerParent.clientWidth || 0) - (pageContainer.clientWidth || 0), 0)
}

export const subscribeScrollbarWidth = (
	element: HTMLElement,
	callback: (scrollbarWidth: number | undefined) => void,
): (() => void) => {
	const observer = new ResizeObserver(() => {
		callback(getScrollbarWidth(element))
	})

	observer.observe(element)

	return () => {
		observer.unobserve(element)
		observer.disconnect()
	}
}

export const mergeRefs =
	<T = unknown>(...refs: (React.MutableRefObject<T> | React.LegacyRef<T>)[]): React.RefCallback<T> =>
	value =>
		refs.forEach(ref => {
			if (typeof ref === 'function') {
				ref(value)
			} else if (ref !== null && !!ref) {
				;(ref as React.MutableRefObject<T | null>).current = value
			}
		})

export const addToHistoryState = (state: Record<string, string | number>) => {
	const historyState = {}
	const { location } = ionHistory
	Object.assign(historyState, location?.state)

	ionHistory.replace(location.pathname + location.search + location.hash, { ...historyState, ...state })
}

export const loadingTimeout = (loading: boolean, onUpdate: (val: boolean) => void, timeout?: number) => {
	if (loading) onUpdate(loading)
	else
		setTimeout(() => {
			onUpdate(loading)
		}, timeout || 1000)
}

export const defineUserMobileDeviceOS = (): 'Android' | 'IOS' | 'Other' => {
	const os = getUAParser().getOS()

	if (os.name === 'Android') {
		return 'Android'
	}

	if (os.name === 'iOS') {
		return 'IOS'
	}

	return 'Other'
}

export const isIOSorAndroid = (): boolean => {
	const os = defineUserMobileDeviceOS();
	return os === 'Android' || os === 'IOS';
}

const get100vh = (): number => {
	const measureDiv = document.createElement('div');
	measureDiv.style.height = '100vh';
	document.body.appendChild(measureDiv);
	const hundredVh = measureDiv.clientHeight;
	document.body.removeChild(measureDiv);
	return hundredVh;
}

const getIsLandscape = (): boolean => {
	if (screen.orientation) {
		return screen.orientation.type.includes('landscape');
	}
	return window.innerWidth > window.innerHeight;
}

export const shouldShowAppBanner = (): boolean => {
	const os = defineUserMobileDeviceOS();
	if (os === 'IOS') {
		if (!isIOSSafariBrowser()) {
			return true
		}
		// see https://docs.google.com/spreadsheets/d/1zfQRwX7YbmrFFcazwv4J2zuoo28WrLM4dURF7xpkeUQ/edit?usp=sharing
		const deviceType = getUAParser().getDevice().type;
		const browserChromeSize = get100vh() - window.innerHeight;
		if (deviceType === 'tablet') {
			if (browserChromeSize > 31) {
				// iPad: browser chrome is too big, Smart App Banner already shown
				return false
			}
			return true
		}
		else if (deviceType === 'mobile') {
			const isLandscape = getIsLandscape();
			if (isLandscape) {
				if (browserChromeSize > 50) {
					// iPhone Landscape: browser chrome is too big, Smart App Banner already shown
					return false
				}
				return true
			} else {
				if (browserChromeSize > 82) {
					// iPhone Portrait: browser chrome is too big, Smart App Banner already shown
					return false
				}
				return true
			}
		}
		else {
			// maybe this is Apple Vision Pro?
			return true
		}
	}
	if (os === 'Android') {
		return true
	}
	return false
}

export const getDemoModeState = (): boolean => {
	let demoModeEnabled = sessionStorage.getItem('demo_mode') === 'true'
	if (!demoModeEnabled) {
		demoModeEnabled = new URLSearchParams(window.location.search).get('demo') === 'true'
	}
	// Set to session storage if not exist and enabled
	if (demoModeEnabled) {
		sessionStorage.setItem('demo_mode', `${demoModeEnabled}`)
	}
	return demoModeEnabled
}
